/**
 * 依存を増やさずに描く軽量チャート。
 *
 * 05-ui は recharts を挙げているが、admin の依存には入っていない。
 * ここで必要なのは「増減の形が分かること」だけで、凡例もツールチップも
 * 軸ラベルの自動配置も要らないため、素の SVG で足りる。
 * 本格的な可視化が必要になったら recharts を足してこのファイルを置き換える。
 */
import * as React from 'react';
import { cn, formatCount, formatDateTime } from '@mail-sv/ui';

export interface SeriesPoint {
  at: string;
  /**
   * `null` は**欠測**（報告が届いていない）。0 とは区別して線を切る。
   *
   * 0 で埋めて線を繋ぐと、エージェントが止まっている間も「平常（0 件）」に
   * 見えてしまう。運用画面でいちばん見落としてはいけないのがそれなので、
   * 欠測は必ず穴として描く。
   */
  value: number | null;
}

export interface Series {
  key: string;
  label: string;
  points: SeriesPoint[];
  /** トークン由来のクラス名（`stroke-primary` 等）。生のパレット色は使わない。 */
  className: string;
}

const VIEW_W = 600;
const VIEW_H = 160;
const PAD = 4;

/** 複数系列の折れ線。値域は全系列で共有する（同じ単位の系列だけ渡すこと）。 */
export function LineChart({
  series,
  height = 180,
  formatValue = formatCount,
  emptyLabel = 'データがありません',
  className,
}: {
  series: Series[];
  height?: number;
  formatValue?: (n: number) => string;
  emptyLabel?: string;
  className?: string;
}): React.ReactElement {
  const all = series.flatMap((s) => s.points);
  // 欠測（null）は値域に入れない。`Math.max(m, null)` は 0 になるので黙って潰れる。
  const max = all.reduce((m, p) => (p.value === null ? m : Math.max(m, p.value)), 0);
  const first = all[0];
  const last = all[all.length - 1];
  // 1 点でも実測があるか。全部欠測なら「データがありません」と同じ扱いにする。
  const hasValue = all.some((p) => p.value !== null);

  if (all.length === 0 || !hasValue) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-panel-elevated/50 text-sm text-muted-foreground',
          className,
        )}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="overflow-x-auto rounded-lg border border-border bg-panel-elevated p-3">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          style={{ height, minWidth: '100%' }}
          role="img"
          aria-label={series.map((s) => s.label).join(' / ')}
        >
          {/* 目盛は 4 本だけ。運用画面で読むのは「跳ねたかどうか」なので細かい格子は要らない。 */}
          {[0, 1, 2, 3].map((i) => {
            const y = PAD + ((VIEW_H - PAD * 2) / 3) * i;
            return <line key={i} x1={0} x2={VIEW_W} y1={y} y2={y} className="stroke-border" strokeWidth={0.5} />;
          })}
          {series.flatMap((s) =>
            // 欠測で切れた区間ごとに 1 本ずつ描く（穴を跨いで繋がない）。
            toPolylines(s.points, max).map((d, i) => (
              <polyline
                key={`${s.key}-${i}`}
                points={d}
                fill="none"
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className={s.className}
              />
            )),
          )}
        </svg>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-3">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <svg width="14" height="4" aria-hidden>
                <line x1="0" y1="2" x2="14" y2="2" strokeWidth="2" className={s.className} />
              </svg>
              {s.label}
            </span>
          ))}
        </div>
        <span className="tabular">
          最大 {formatValue(max)}
          {first && last ? ` / ${formatDateTime(first.at)} 〜 ${formatDateTime(last.at)}` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * 系列を **欠測で分割した** polyline の配列にする。
 *
 * x 位置は**元の配列の添字**で決める（欠測を詰めない）。詰めると同じチャートに
 * 並ぶ系列どうしで時間軸がずれ、「認証失敗が跳ねたのと BAN が増えたのは
 * 同じ時刻か」を読めなくなる。
 */
function toPolylines(points: SeriesPoint[], max: number): string[] {
  if (points.length === 0) return [];
  const usableH = VIEW_H - PAD * 2;
  const step = points.length > 1 ? VIEW_W / (points.length - 1) : 0;
  const scale = max > 0 ? usableH / max : 0;

  const out: string[] = [];
  let run: string[] = [];
  // 孤立した 1 点は同じ座標を 2 つ並べる。線分が無いと `strokeLinecap="round"` の
  // 点として描かれず、**単発の跳ね（1 時間だけ BAN が出た等）が消える**。
  const flush = (): void => {
    if (run.length === 1) out.push(`${run[0]} ${run[0]}`);
    else if (run.length > 1) out.push(run.join(' '));
    run = [];
  };
  points.forEach((p, i) => {
    if (p.value === null) {
      flush();
      return;
    }
    run.push(`${(i * step).toFixed(2)},${(VIEW_H - PAD - p.value * scale).toFixed(2)}`);
  });
  flush();
  return out;
}
